import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setupApiInterceptors } from '../services/api'
import { petService } from '../services/petService'
import { Pet, CreatePetDto, UpdatePetDto } from '../types/Pet'
import LoadingSpinner from '../components/Common/LoadingSpinner'
import AddEditPetModal from '../components/Pets/AddEditPetModal'
import SharePetModal from '../components/SharePetModal'

export default function Pets() {
  const { getAccessTokenSilently } = useAuth0()
  const [pets, setPets] = useState<Pet[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingPet, setEditingPet] = useState<Pet | null>(null)
  const [showScanReports, setShowScanReports] = useState<Pet | null>(null)
  const [showShare, setShowShare] = useState<Pet | null>(null)

  useEffect(() => {
    setupApiInterceptors(getAccessTokenSilently)
    loadPets()
  }, [getAccessTokenSilently])

  const loadPets = async () => {
    try {
      const data = await petService.getPets()
      setPets(data)
    } catch (error) {
      console.error('Failed to load pets:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSavePet = async (petData: CreatePetDto) => {
    await petService.createPet(petData)
    await loadPets()
    setShowAddModal(false)
  }

  const handleUpdatePet = async (petData: CreatePetDto) => {
    if (!editingPet) return
    await petService.updatePet(editingPet.id, petData as UpdatePetDto)
    await loadPets()
    setEditingPet(null)
  }

  const handleToggleLost = async (pet: Pet) => {
    try {
      await petService.setLostStatus(pet.id, !pet.isLost)
      await loadPets()
    } catch (error) {
      console.error('Failed to update lost status:', error)
    }
  }

  if (loading) return <LoadingSpinner />

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">My Pets</h1>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          Add Pet
        </button>
      </div>

      {pets.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 mb-4">You haven't added any pets yet</p>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            Add Your First Pet
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pets.map(pet => (
            <div key={pet.id} className="card hover:shadow-lg transition-shadow relative">
              <button
                onClick={() => setEditingPet(pet)}
                className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Edit pet"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>

              {pet.photoUrl && (
                <img src={pet.photoUrl} alt={pet.name} className="w-full h-48 object-cover rounded-lg mb-4" />
              )}
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xl font-bold">{pet.name}</h3>
                {pet.isLost && (
                  <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded">LOST</span>
                )}
              </div>
              <p className="text-gray-600">{pet.species}</p>
              {pet.breed && <p className="text-gray-500 text-sm">{pet.breed}</p>}
              {pet.tagCode && (
                <p className="text-green-600 text-sm mt-2 font-semibold">Tag: {pet.tagCode}</p>
              )}

              <div className="mt-4 space-y-2">
                {pet.isLost ? (
                  <>
                    <button className="btn-secondary w-full" onClick={() => handleToggleLost(pet)}>Mark Found</button>
                    <button className="btn-primary w-full text-sm" onClick={() => setShowScanReports(pet)}>View Scan Reports</button>
                  </>
                ) : (
                  <button className="btn-danger w-full" onClick={() => handleToggleLost(pet)}>Report Lost</button>
                )}
                <button className="btn-secondary w-full text-sm" onClick={() => setShowShare(pet)}>Share with Family</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddEditPetModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSavePet}
      />

      {editingPet && (
        <AddEditPetModal
          isOpen={true}
          onClose={() => setEditingPet(null)}
          onSave={handleUpdatePet}
          onDelete={async () => {
            await petService.deletePet(editingPet.id)
            setEditingPet(null)
            await loadPets()
          }}
          pet={editingPet}
        />
      )}

      {showScanReports && (
        <ScanReportsModal
          pet={showScanReports}
          onClose={() => setShowScanReports(null)}
        />
      )}

      {showShare && (
        <SharePetModal
          isOpen={true}
          onClose={() => setShowShare(null)}
          petId={showShare.id}
          petName={showShare.name}
        />
      )}
    </div>
  )
}

interface ScanReport {
  id: string
  tagCode: string
  petId: string
  petName?: string
  scannedAt: string
  latitude?: number
  longitude?: number
  address?: string
  finderName?: string
  finderPhone?: string
  mapUrl?: string
}

function ScanReportsModal({ pet, onClose }: { pet: Pet; onClose: () => void }) {
  const [reports, setReports] = useState<ScanReport[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    petService.getScanReports(pet.id)
      .then(setReports)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [pet.id])

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Scan Reports — {pet.name}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading...</p>
          ) : reports.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No scan reports yet</p>
          ) : (
            <div className="space-y-4">
              {reports.map(report => (
                <div key={report.id} className="border border-gray-100 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-800">
                    {new Date(report.scannedAt).toLocaleString()}
                  </p>
                  {report.address && (
                    <p className="text-sm text-gray-600 mt-1">&#128205; {report.address}</p>
                  )}
                  {report.finderName && (
                    <p className="text-sm text-gray-600 mt-1">&#128100; {report.finderName}</p>
                  )}
                  <div className="flex gap-2 mt-3">
                    {report.mapUrl && (
                      <a href={report.mapUrl} target="_blank" rel="noopener noreferrer"
                        className="text-xs px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100">
                        Open in Maps
                      </a>
                    )}
                    {report.finderPhone && (
                      <>
                        <a href={`tel:${report.finderPhone}`}
                          className="text-xs px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100">
                          Call
                        </a>
                        <a href={`sms:${report.finderPhone}`}
                          className="text-xs px-3 py-1.5 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100">
                          Text
                        </a>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
